import FeedCard from "../../Home/main-content/feed/feed-card";
import UserCard from "./userCard";
import { useAppDispatch, useAppSelector } from "../../../hooks/store-hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGetSearchDataQuery } from "../../../store/api/searchApi";
import { BtnLoader } from "../../../icons";
import { useLocation } from "react-router-dom";
import { setSelectedType } from "../../../store/slices/searchSlice";

const preComputeFollowData = (posts: any) => {
  if (posts?.length === 0) {
    return {};
  }
  return posts.reduce((acc: any, post: any) => {
    acc[post?.user?.id] = post?.user?.is_follows;
    return acc;
  }, {});
};

const MainSearchContent = ({ userCity }: { userCity?: string[] }) => {
  const dispatch = useAppDispatch();
  const observer = useRef<IntersectionObserver | null>(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchQuery = queryParams.get("q") || "";
  const { selectedType } = useAppSelector((state: any) => state.search || {});

  const [userOffset, setUserOffset] = useState(0);
  const [postOffset, setPostOffset] = useState(0);
  const [posts, setPosts] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [isEnd, setIsEnd] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  // Fetch search data based on type (post or user)
  const {
    data: searchData,
    error: searchError,
    isLoading: isSearchLoading,
  } = useGetSearchDataQuery(
    {
      q: searchQuery,
      type: selectedType,
      limit: 9,
      offset: selectedType === "post" ? postOffset : userOffset,
      ...(selectedType !== "post" && { city: userCity?.join(",") }),
    },
    { refetchOnMountOrArgChange: true },
  );

  const memoFollowData = useMemo(() => preComputeFollowData(posts), [posts]);
  const [isFollow, setIsFollow] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    setIsFollow(memoFollowData);
  }, [memoFollowData]);

  // Append new search data when available based on selected type
  useEffect(() => {
    if (firstLoad && searchData?.data?.users?.length === 0) {
      dispatch(setSelectedType("post"));
      setFirstLoad(false);
    }
    if (firstLoad && searchData?.data?.posts?.length === 0) {
      dispatch(setSelectedType("user"));
      setFirstLoad(false);
    }
    if (selectedType === "post" && searchData?.data?.posts) {
      setPosts((prevPosts) => [...prevPosts, ...searchData.data.posts]);
      setIsEnd(searchData?.data?.is_end);
    } else if (selectedType === "user" && searchData?.data?.users) {
      setUsers((prevUsers) => [...prevUsers, ...searchData.data.users]);
      setIsEnd(searchData?.data?.is_end);
    }
  }, [searchData, selectedType]);

  // Observer callback for infinite scrolling
  const lastItemElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isSearchLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !isEnd) {
          if (selectedType === "post") {
            setPostOffset((prevOffset) => prevOffset + 9);
          } else if (selectedType === "user") {
            setUserOffset((prevOffset) => prevOffset + 9);
          }
        }
      });

      if (node) observer.current.observe(node);
    },
    [isSearchLoading, isEnd, selectedType],
  );

  // Reset offsets when selectedType or search query changes
  useEffect(() => {
    setPostOffset(0);
    setUserOffset(0);
    setPosts([]);
    setUsers([]);
  }, [selectedType, searchQuery, userCity]);

  return (
    <div className="lg:col-span-6 rounded-lg h-min">
      <div className="text-[24px] font-semibold mb-4">
        {selectedType === "post"
          ? "Post search results"
          : "People search results"}
      </div>
      <div className="space-y-4">
        {isSearchLoading && posts.length === 0 && users.length === 0 ? (
          <div>Loading Search Data...</div>
        ) : searchError ? (
          <div>Error occurred: {searchError.message}</div>
        ) : selectedType === "post" ? (
          posts.length > 0 ? (
            posts.map((post: any) => (
              <FeedCard
                key={post.id}
                postData={post}
                follow={isFollow[post?.user?.id]}
                setIsFollow={(followStatus: boolean) =>
                  setIsFollow((prev) => ({
                    ...prev,
                    [post.user.id]: followStatus,
                  }))
                }
              />
            ))
          ) : (
            <div className="text-center text-gray-500 flex items-center">
              We couldn't find any posts that match your search criteria. Please
              try adjusting your filters or check back later for new posts.
            </div>
          )
        ) : users.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {users.map((user: any) => (
              <UserCard
                userId={user.id}
                firstName={user.first_name}
                lastName={user.last_name}
                title={user.designation}
                location={user.city || ""}
                imageSrc={user.profile_picture}
                following={user.following}
                self={user.self}
                banner={user?.banner}
                is_opc={user?.memberships?.is_opc}
                badge={user?.badge}
              />
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500">
            We couldn't find any users that match your search criteria. Please
            try adjusting your filters or check back later for new users.
          </div>
        )}
      </div>
      {!isEnd && (
        <div
          className="w-full flex justify-center my-4"
          ref={lastItemElementRef}
        >
          <BtnLoader />
        </div>
      )}
    </div>
  );
};

export default MainSearchContent;
